import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Phone, AlertTriangle, Search, CheckCircle, Clock, TrendingUp, FileSpreadsheet, PhoneCall, CalendarClock, AlertCircle, Mail, MapPinned, Award, Activity, Target } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import AlertsWidget from '@/components/AlertsWidget';
import TopCallsWidget from '@/components/TopCallsWidget';
import OpportunitiesTodayWidget from '@/components/OpportunitiesTodayWidget';
import DailyCoachWidget from '@/components/DailyCoachWidget';
import CiblesPotentielWidget from '@/components/CiblesPotentielWidget';
import MyObjectiveWidget from '@/components/MyObjectiveWidget';
import AutomationsStatusCard from '@/components/AutomationsStatusCard';
import WeeklySummaryStrip from '@/components/WeeklySummaryStrip';
import MorningEmailsAlertBanner from '@/components/MorningEmailsAlertBanner';

const PIE_COLORS = ['#0055FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280', '#14B8A6'];

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openProspect = (id) => navigate(`/prospects?prospect=${id}`);
  const [stats, setStats] = useState(null);
  const [relanceData, setRelanceData] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [coachEnabled, setCoachEnabled] = useState(false);
  const [commercials, setCommercials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/relances/dashboard'),
      api.get('/dashboard/team-stats'),
      api.get('/settings').catch(() => ({ data: {} })),
      api.get('/commercials').catch(() => ({ data: [] })),
    ]).then(([statsRes, relancesRes, teamRes, settingsRes, comRes]) => {
      setStats(statsRes.data);
      setRelanceData(relancesRes.data);
      setTeamStats(teamRes.data);
      setCoachEnabled((settingsRes.data?.feature_coach_du_jour_enabled || 'false') === 'true');
      setCommercials(comRes.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const markRelanceDone = async (relanceId) => {
    try {
      await api.put(`/relances/${relanceId}`, { statut: 'effectuee' });
      toast.success('Relance effectuée');
      const { data } = await api.get('/relances/dashboard');
      setRelanceData(data);
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="animate-pulse-subtle text-center py-20 text-[#71717A]">Chargement du dashboard...</div>;
  if (!stats) return <div className="text-center py-20 text-[#71717A]">Erreur de chargement</div>;

  const isCommercial = user?.role === 'commercial';
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  const kpis = [
    { label: 'Total Prospects', value: stats.total_prospects, icon: Users, color: '#0055FF' },
    { label: 'Numéros trouvés', value: stats.trouves, icon: Phone, color: '#10B981' },
    { label: 'À vérifier', value: stats.a_verifier, icon: AlertTriangle, color: '#F59E0B' },
    { label: 'Non trouvés', value: stats.non_trouves, icon: Search, color: '#EF4444' },
    { label: 'Traités', value: stats.traites, icon: CheckCircle, color: '#8B5CF6' },
    { label: 'Rappels du jour', value: relanceData?.nb_aujourd_hui || 0, icon: Clock, color: '#EC4899' },
  ];

  const perfData = (stats.performance_commerciaux || []).map(p => ({
    name: p.nom?.split(' ')[0] || 'N/A', total: p.total, rdv: p.rdv, convertis: p.convertis
  }));

  const statusLabels = {
    a_appeler: 'À appeler', appele: 'Appelé', rdv_obtenu: 'RDV obtenu',
    refus: 'Refus', rappel_demande: 'Rappel', injoignable: 'Injoignable',
    mauvais_numero: 'Mauvais n°', converti: 'Converti', nouveau: 'Nouveau'
  };
  const pieData = Object.entries(stats.statuts_commerciaux || {}).map(([k, v]) => ({
    name: statusLabels[k] || k, value: v
  })).filter(d => d.value > 0);

  const TypeIcon = (type) => type === 'email' ? Mail : type === 'visite' ? MapPinned : PhoneCall;

  const RelanceRow = ({ r, isOverdue }) => (
    <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${isOverdue ? 'bg-[#FEF2F2] border-[#EF4444]/20' : 'bg-white border-[#E5E7EB]'}`}
      data-testid={`dashboard-relance-${r.id}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isOverdue ? 'bg-[#EF4444]/10' : 'bg-[#F97316]/10'}`}>
        {React.createElement(TypeIcon(r.type_relance), { className: `w-4 h-4 ${isOverdue ? 'text-[#EF4444]' : 'text-[#F97316]'}` })}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{r.prospect_enseigne} - {r.prospect_nom}</p>
        <p className="text-xs text-[#71717A]">
          {r.prospect_ville} {r.prospect_cp && `(${r.prospect_cp})`}
          {r.prospect_telephone && r.prospect_telephone !== '?' && (
            <> &bull; <a href={`tel:${r.prospect_telephone.replace(/\s/g, '')}`} className="text-[#0055FF] hover:underline">{r.prospect_telephone}</a></>
          )}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs font-medium">
          {r.creneau ? r.creneau : new Date(r.date_relance).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
        </p>
        <p className="text-[10px] text-[#71717A] capitalize">{r.type_relance}</p>
      </div>
      <Button size="sm" variant="outline" onClick={() => markRelanceDone(r.id)}
        className="h-7 text-xs gap-1 text-[#10B981] border-[#10B981]/30 hover:bg-[#10B981]/5" data-testid={`done-relance-${r.id}`}>
        <CheckCircle className="w-3 h-3" /> Fait
      </Button>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Hero header with gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#F97316] via-[#EA580C] to-[#1A1A1A] p-7 text-white shadow-lg">
        <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
        <div className="absolute -top-8 -left-8 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
        <div className="relative flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/70 font-medium">Tableau de bord</p>
            <h1 className="text-3xl font-bold tracking-tight mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
              Bonjour, {user?.prenom} 👋
            </h1>
            <p className="text-sm text-white/80 mt-1">
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {' · '}
              {stats.total_prospects} prospects · {stats.traites} traités
            </p>
          </div>
          <div className="hidden md:flex gap-3 text-right">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 min-w-[110px]">
              <p className="text-[10px] uppercase tracking-wider text-white/70">Rappels jour</p>
              <p className="text-2xl font-bold tabular-nums">{relanceData?.nb_aujourd_hui || 0}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 min-w-[110px]">
              <p className="text-[10px] uppercase tracking-wider text-white/70">En retard</p>
              <p className="text-2xl font-bold tabular-nums text-[#FECACA]">{relanceData?.nb_en_retard || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {coachEnabled && !isCommercial && <DailyCoachWidget onProspectClick={openProspect} />}

      {/* Statut des automatisations — admin uniquement */}
      {isAdmin && <MorningEmailsAlertBanner />}
      {isAdmin && <AutomationsStatusCard />}
      {isAdmin && <WeeklySummaryStrip />}

      {/* Mon objectif du mois — commerciaux uniquement */}
      {isCommercial && <MyObjectiveWidget />}

      {/* Opportunités du jour — en haut, prominent */}
      <OpportunitiesTodayWidget />

      {/* Alertes — full width si présentes */}
      <AlertsWidget onProspectClick={openProspect} />

      {/* Top 10 (2/3) + Potentiel commercial (1/3) — Potentiel masqué pour commerciaux */}
      <div className={`grid grid-cols-1 ${isCommercial ? '' : 'lg:grid-cols-3'} gap-4`}>
        <div className={isCommercial ? '' : 'lg:col-span-2'}>
          <TopCallsWidget limit={10} onProspectClick={openProspect} />
        </div>
        {!isCommercial && <CiblesPotentielWidget />}
      </div>

      {/* Répartition commerciale — masqué pour commerciaux */}
      {!isCommercial && commercials.length > 0 && (
        <Card className="border border-[#E5E7EB] shadow-sm" data-testid="commercials-repartition">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-[#F97316]" />
                <h3 className="text-sm font-semibold">Répartition par commercial</h3>
              </div>
              <span className="text-xs text-[#71717A]">{commercials.length} commerciaux actifs</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {commercials.map(c => {
                const count = (stats.par_commercial && stats.par_commercial[c.id]) || 0;
                const percent = stats.total_prospects > 0 ? Math.round(count * 100 / stats.total_prospects) : 0;
                return (
                  <div key={c.id} className="rounded-xl p-4 border border-[#E5E7EB] bg-white hover:shadow-md transition-shadow"
                    data-testid={`commercial-card-${c.id}`}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                        style={{ background: c.color || '#71717A' }}>
                        {c.prenom?.[0]}{c.nom?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">{c.prenom} {c.nom}</p>
                        <p className="text-[10px] text-[#71717A] truncate">{(c.departments || []).length} dpts</p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-baseline justify-between">
                      <span className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>{count}</span>
                      <span className="text-[10px] text-[#71717A]">prospects · {percent}%</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${percent}%`, background: c.color || '#71717A' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards — masqué pour commerciaux (vue épurée) */}
      {!isCommercial && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 stagger-children" data-testid="kpi-grid">
          {kpis.map((kpi, i) => (
            <Card key={i} className="kpi-card border border-[#E5E7EB] shadow-sm">
              <CardContent className="p-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${kpi.color}10` }}>
                  <kpi.icon className="w-[18px] h-[18px]" style={{ color: kpi.color }} />
                </div>
                <p className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                  {(kpi.value || 0).toLocaleString()}
                </p>
                <p className="text-xs text-[#71717A] mt-0.5">{kpi.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* En retard */}
        <Card className={`border shadow-sm ${relanceData?.nb_en_retard > 0 ? 'border-[#EF4444]/30 bg-[#FEF2F2]/30' : 'border-[#E5E7EB]'}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <AlertCircle className={`w-4 h-4 ${relanceData?.nb_en_retard > 0 ? 'text-[#EF4444]' : 'text-[#71717A]'}`} />
                Appels en retard
              </h3>
              {relanceData?.nb_en_retard > 0 && (
                <Badge className="bg-[#EF4444] text-white text-xs">{relanceData.nb_en_retard}</Badge>
              )}
            </div>
            {(!relanceData?.en_retard || relanceData.en_retard.length === 0) ? (
              <p className="text-sm text-[#71717A] text-center py-6">Aucun appel en retard</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {relanceData.en_retard.map(r => <RelanceRow key={r.id} r={r} isOverdue={true} />)}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Aujourd'hui */}
        <Card className={`border shadow-sm ${relanceData?.nb_aujourd_hui > 0 ? 'border-[#F97316]/30' : 'border-[#E5E7EB]'}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-[#F97316]" />
                Rappels du jour
              </h3>
              {relanceData?.nb_aujourd_hui > 0 && (
                <Badge className="bg-[#F97316] text-white text-xs">{relanceData.nb_aujourd_hui}</Badge>
              )}
            </div>
            {(!relanceData?.aujourd_hui || relanceData.aujourd_hui.length === 0) ? (
              <p className="text-sm text-[#71717A] text-center py-6">Aucun rappel prévu aujourd'hui</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {relanceData.aujourd_hui.map(r => <RelanceRow key={r.id} r={r} isOverdue={false} />)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* À venir cette semaine */}
      {relanceData?.a_venir && relanceData.a_venir.length > 0 && (
        <Card className="border border-[#E5E7EB] shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-[#0055FF]" />
              À venir cette semaine
              <Badge variant="secondary" className="text-xs">{relanceData.nb_a_venir}</Badge>
            </h3>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {relanceData.a_venir.map(r => <RelanceRow key={r.id} r={r} isOverdue={false} />)}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Chart — masqué pour commerciaux */}
        {!isCommercial && perfData.length > 0 && (
          <Card className="border border-[#E5E7EB] shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#0055FF]" />
                Performance Commerciaux
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={perfData}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#0055FF" radius={[4, 4, 0, 0]} name="Total" />
                  <Bar dataKey="rdv" fill="#10B981" radius={[4, 4, 0, 0]} name="RDV" />
                  <Bar dataKey="convertis" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Convertis" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Status Pie — masqué pour commerciaux */}
        {!isCommercial && pieData.length > 0 && (
          <Card className="border border-[#E5E7EB] shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold mb-4">Répartition Statuts</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                    paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* TEAM DASHBOARD — collective per-user analysis (masqué pour commerciaux) */}
      {!isCommercial && teamStats?.team && teamStats.team.length > 0 && (
        <Card className="border border-[#E5E7EB] shadow-sm" data-testid="team-dashboard-section">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Award className="w-4 h-4 text-[#F97316]" />
                Analyse par intervenant
                <Badge variant="secondary" className="bg-[#F4F4F5] text-[#71717A] text-xs">{teamStats.team.length}</Badge>
              </h3>
              <span className="text-xs text-[#71717A]">Vue collective — tout le monde voit les mêmes stats</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="team-cards-grid">
              {teamStats.team.map((u, idx) => {
                const initials = `${(u.prenom || '?')[0]}${(u.nom || '')[0] || ''}`.toUpperCase();
                const colors = ['#F97316', '#0055FF', '#10B981', '#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#EF4444'];
                const color = colors[idx % colors.length];
                return (
                  <div key={u.user_id} className="border border-[#E5E7EB] rounded-lg p-4 hover:shadow-md transition-shadow bg-white" data-testid={`team-card-${u.user_id}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ backgroundColor: color }}>
                        {initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{u.nom_complet || 'Utilisateur'}</p>
                        <p className="text-xs text-[#71717A] truncate">{u.role?.replace('_', ' ')}</p>
                      </div>
                      {u.actions_aujourdhui > 0 && (
                        <Badge className="bg-[#ECFDF5] text-[#059669] text-[10px]" title="Actions aujourd'hui">
                          <Activity className="w-2.5 h-2.5 mr-1" />
                          {u.actions_aujourdhui}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-[#F4F4F5]">
                      <div>
                        <p className="text-lg font-bold text-[#0055FF]" style={{ fontFamily: 'var(--font-heading)' }}>{u.prospects_assignes}</p>
                        <p className="text-[10px] text-[#71717A] uppercase tracking-wide">Prospects</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-[#10B981]" style={{ fontFamily: 'var(--font-heading)' }}>{u.rdv}</p>
                        <p className="text-[10px] text-[#71717A] uppercase tracking-wide">RDV</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-[#8B5CF6]" style={{ fontFamily: 'var(--font-heading)' }}>{u.convertis}</p>
                        <p className="text-[10px] text-[#71717A] uppercase tracking-wide">Convertis</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#F4F4F5] space-y-1 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-[#71717A] flex items-center gap-1"><PhoneCall className="w-3 h-3" />Appels effectués</span>
                        <span className="font-medium">{u.appels_effectues}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#71717A] flex items-center gap-1"><CalendarClock className="w-3 h-3" />Relances créées</span>
                        <span className="font-medium">{u.relances_creees}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#71717A] flex items-center gap-1"><Target className="w-3 h-3" />Taux conversion</span>
                        <span className={`font-semibold ${u.taux_conversion >= 30 ? 'text-[#10B981]' : u.taux_conversion >= 10 ? 'text-[#F59E0B]' : 'text-[#71717A]'}`}>
                          {u.taux_conversion}%
                        </span>
                      </div>
                      {(u.refus > 0 || u.injoignables > 0) && (
                        <div className="flex items-center justify-between text-[10px] text-[#71717A]">
                          <span>Refus {u.refus} • Injoignables {u.injoignables}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Imports — masqué pour commerciaux */}
      {!isCommercial && (
        <Card className="border border-[#E5E7EB] shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-[#0055FF]" />
              Imports Récents
            </h3>
            {(stats.recent_imports || []).length === 0 ? (
              <p className="text-sm text-[#71717A] text-center py-6">Aucun import</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="recent-imports-table">
                  <thead>
                    <tr className="border-b border-[#E5E7EB]">
                      <th className="text-left py-2 px-3 text-[#71717A] font-medium">Fichier</th>
                      <th className="text-left py-2 px-3 text-[#71717A] font-medium">Lignes</th>
                      <th className="text-left py-2 px-3 text-[#71717A] font-medium">Statut</th>
                      <th className="text-left py-2 px-3 text-[#71717A] font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_imports.map(imp => (
                      <tr key={imp.id} className="border-b border-[#E5E7EB]/50">
                        <td className="py-2 px-3 font-medium">{imp.nom_fichier}</td>
                        <td className="py-2 px-3">{imp.nb_lignes}</td>
                        <td className="py-2 px-3">
                          <Badge variant="secondary" className={`text-xs status-${imp.statut}`}>
                            {imp.statut === 'termine' ? 'Terminé' : imp.statut === 'en_cours' ? 'En cours' : imp.statut}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 text-[#71717A]">{new Date(imp.date_import).toLocaleDateString('fr-FR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
